import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { RWA_COIN_ICON } from "~/consts";
import { TooltipContent } from "./TooltipContent";
import ReactDOMServer from "react-dom/server";
import {
    DEFAULT_AXIS_LABEL_PROPS,
    DEFAULT_AXIS_PROPS,
    DEFAULT_TOOLTIP_PROPS,
} from "../../consts";
import { AggregateItem } from "~/modules/token_timeseries/types";
import { roundN } from "~/utils/functions/roundN";
import { RWA_COLORS } from "~/styles/colors";

// // // //

export interface NetFlowsChartItem extends AggregateItem {
    content: {
        name: string;
        ticker: string;
        icon_url: string;
        protocol_name: string;
        protocol_icon_url: string;
        issuer_name: string;
        issuer_icon_url: string;
    };
}

// // // //

// NOTE - this is a copy of formatCurrencyValueCompact, except with 0 fractional digits and integrated signage + $ symbol
function formatCurrencyValueCompactAxis(value: number): string {
    // Positive or negative?
    const absValue = Math.abs(value);
    const isNegative = absValue !== value;

    // Handle positive/negative signage
    const signage = isNegative ? "-" : "";

    // Handle billion or greater
    if (absValue >= 1_000_000_000) {
        return `${signage}$${roundN(absValue / 1_000_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}B`;
    }
    // Handle million or greater but less than a billion
    else if (absValue >= 1_000_000) {
        return `${signage}$${roundN(absValue / 1_000_000, 1).toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}M`;
    }
    // Handle thousand or greater but less than a million
    else {
        return `${signage}$${roundN(absValue / 1_000, 1).toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}K`;
    }
}

// // // //

function getChartOptions(props: { items: NetFlowsChartItem[] }) {
    const { items } = props;

    const emptySeriesData = Array(items.length).fill(null);

    const series = items
        .map((item) => {
            return {
                name: item.content.name,
                ticker: item.content.ticker,
                icon_url: item.content.icon_url || RWA_COIN_ICON,
                data: [...emptySeriesData],
                net_flows_value: item.value,
                color: item.group.color,
                protocol_name: item.content.protocol_name,
                protocol_icon_url: item.content.protocol_icon_url,
                issuer_name: item.content.issuer_name,
                issuer_icon_url: item.content.issuer_icon_url,
            };
        })
        .sort((a, b) => b.net_flows_value - a.net_flows_value)
        .map((series, index) => {
            const data = [...emptySeriesData];

            data[index] = series.net_flows_value;

            return {
                ...series,
                data,
            };
        });

    // Build categories
    // NOTE - this MUST match the order in the series array
    const categories = series.map((item) => item.name);

    const opts = {
        chart: {
            type: "bar",
            responsive: true,
            height: 390,
            backgroundColor: "transparent",
            animation: false,
        },
        title: {
            text: null, // Hiding the title
        },
        xAxis: {
            lineColor: RWA_COLORS.slate["300"], // Color the axis line
            categories,
            labels: {
                useHTML: true,
                padding: 0, // Add padding around labels
                distance: 30, // Increase distance between labels and axis
                formatter: function (
                    this: Highcharts.AxisLabelsFormatterContextObject,
                ): string {
                    const index = categories.indexOf(this.value.toString());
                    if (index !== -1) {
                        const item = series[index];
                        const ticker = item.ticker;
                        const protocol_name = item.protocol_name;
                        const truncatedTicker =
                            ticker.length > 6
                                ? ticker.slice(0, 6) + "..."
                                : ticker;

                        const truncatedProtocolName =
                            protocol_name.length > 10
                                ? protocol_name.slice(0, 10) + "..."
                                : protocol_name;

                        return `<div style="display: flex; justify-content: start; align-items: center; min-width: 80px; padding: 0 5px;">
                                <div style="display: flex; flex-direction: column; align-items: start;">
                                    <span style="white-space: nowrap; overflow: visible; font-size: 12px;">${truncatedTicker}</span>
                                    <span style="white-space: nowrap; overflow: visible; font-size: 11px; color: #64748b;">${truncatedProtocolName}</span>
                                </div>
                            </div>`;
                    }
                    return this.value.toString();
                },
            },
        },
        yAxis: {
            ...DEFAULT_AXIS_PROPS,
            gridLineColor: RWA_COLORS.slate["300"],
            min: Math.min(...series.map((s) => s.net_flows_value)),
            max: Math.max(...series.map((s) => s.net_flows_value)),
            title: {
                text: null,
            },
            labels: {
                ...DEFAULT_AXIS_LABEL_PROPS,
                enabled: true,
                formatter: function (
                    this: Highcharts.AxisLabelsFormatterContextObject,
                ): string {
                    const value = this.value;
                    return `${formatCurrencyValueCompactAxis(Number(value))}`;
                },
            },
        },
        tooltip: {
            useHTML: true,
            shared: true, // Changed to true to show tooltips when hovering on the same Y position
            // // // //
            // Renders the tooltip outside the chart's SVG
            // https://api.highcharts.com/highcharts/tooltip.outside
            outside: true,
            style: {
                zIndex: 225,
                backgroundColor: "white",
            },
            // // // //
            ...DEFAULT_TOOLTIP_PROPS,
            formatter: function (this: any) {
                const points = this.points || [this.point];
                const point = points[0];

                // Get the data for TooltipContent
                const name = point.series.options.name;
                const ticker = point.series.options.ticker;
                const icon_url = point.series.options.icon_url;
                const net_flows_value = point.series.options.net_flows_value;

                const protocol_name = point.series.options.protocol_name;
                const protocol_icon_url =
                    point.series.options.protocol_icon_url;
                const issuer_name = point.series.options.issuer_name;
                const issuer_icon_url = point.series.options.issuer_icon_url;

                return ReactDOMServer.renderToString(
                    <TooltipContent
                        name={name}
                        ticker={ticker}
                        value={net_flows_value}
                        icon_url={icon_url}
                        protocol_name={protocol_name}
                        protocol_icon_url={protocol_icon_url}
                        issuer_name={issuer_name}
                        issuer_icon_url={issuer_icon_url}
                    />,
                );
            },
        },
        accessibility: {
            enabled: false,
        },
        credits: {
            enabled: false,
        },
        plotOptions: {
            series: {
                stacking: "normal",
                stickyTracking: true, // Enable sticky tracking for tooltips
                enableMouseTracking: true, // Ensure mouse tracking is enabled
                states: {
                    inactive: {
                        opacity: 1, // Keep inactive bars at full opacity
                    },
                    hover: {
                        brightness: 0.1, // Reduce brightness change on hover
                    },
                },
            },
            bar: {
                borderWidth: 0,
                pointPadding: 0.1,
                groupPadding: 0.1,
                dataLabels: {
                    enabled: true,
                    align: "right",
                    inside: true,
                    formatter: function (this: any): string {
                        // Use the same formatting as yAxis labels
                        return formatCurrencyValueCompactAxis(Number(this.y));
                    },
                    style: {
                        fontWeight: "500",
                        textOutline: "none",
                        color: "#333333",
                    },
                },
            },
        },
        legend: {
            enabled: false, // Hiding the legend
        },
        series,
    };

    return opts;
}

// // // //

/**
 * Net Flows Chart component for Asset Aggregates
 */
export function AssetNetFlowsChart(props: { items: NetFlowsChartItem[] }) {
    const { items } = props;

    // DEBUG - display empty message
    if (items.length === 0) {
        return <p className="text-red-600 font-semibold text-xl">NO DATA</p>;
    }

    const options = getChartOptions({ items });

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}
